import * as React from "react";
import {
  GridColDef,
  GridRenderCellParams,
  GridRowsProp,
  GridSortModel,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import {
  Link,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import NoResultsOverlay from "../../components/NoResultOverlay";
import useDebounce from "../../hooks/useDebounce";
import { Project, ProjectResource } from "../../types/project";
import { Talent } from "../../types/users";
import { useNavigate } from "react-router-dom";
import { useGetProjectsQuery } from "../../services/project.service";
import dayjs from "dayjs";
import ProjectDrawer from "../project/ProjectDrawer";
import { CardBase, GlassDataGrid, StyledTextField, Button, OrangeLinearProgress } from "../../components/global";

const TalentJobsPanel = ({ data: dataTalent }: { data?: Talent; }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(25);
  const [sortModel, setSortModel] = React.useState<GridSortModel>([]);
  const [searchQuery, setSearchQuery] = React.useState<string | undefined>(
    undefined
  );
  const [open, setOpen] = React.useState(false);
  const [currentItem, setCurrentItem] = React.useState<Project | null>(null);

  const { data, isLoading, isFetching } = useGetProjectsQuery({
    page,
    pageSize,
    sortField: sortModel.length > 0 ? sortModel[0].field : undefined,
    sortMode: sortModel.length > 0 ? sortModel[0].sort : undefined,
    search: searchQuery,
    talents: dataTalent?.id,
  });
  const rowCount = React.useMemo(() => data?.meta.total, [data]);

  const rows: GridRowsProp | undefined = React.useMemo(
    () => data?.data,
    [data]
  );

  const columns: GridColDef[] = React.useMemo(
    () => [
      {
        field: "title",
        headerName: "Job Title",
        flex: 1,
        sortable: true,
        filterable: false,
        minWidth: isMobile ? 200 : 300,
        // valueGetter: (params: GridValueGetterParams<undefined, Project>) =>
        //   `#${params.row.id} - ${params.row.title}`,
        renderCell: (params: GridRenderCellParams<undefined, Project>) => (
          <Link
            component="button"
            variant="body2"
            onClick={() => onView(params.row)}
            sx={{
              color: "#F4A259",
              textDecoration: "none",
              fontSize: { xs: "0.875rem", sm: "0.95rem" },
              "&:hover": {
                textDecoration: "underline",
                color: "#E8944A",
              },
            }}
          >
            #{params.row.id} - {params.row.title}
          </Link>
        ),
      },
      {
        field: "resources.status",
        headerName: "Status",
        minWidth: isMobile ? 80 : 120,
        flex: isMobile ? 0 : undefined,
        sortable: true,
        filterable: true,
        valueGetter: (params: GridValueGetterParams<undefined, Project>) => {
          const resource = params.row.resources?.find(
            (item: ProjectResource) => item.resource_id === dataTalent?.id
          );
          return `${resource?.status?.toUpperCase()}`;
        },
      },
      {
        field: "resources.rate",
        headerName: "Rate",
        minWidth: isMobile ? 80 : 100,
        flex: isMobile ? 0 : undefined,
        sortable: false,
        filterable: false,
        hide: isMobile,
        valueGetter: (params: GridValueGetterParams<undefined, Project>) => {
          const resource = params.row.resources?.find(
            (item: ProjectResource) => item.resource_id === dataTalent?.id
          );
          return params.row.type == "fixed"
            ? `$${resource?.price} (fixed)`
            : `$${resource?.rate}/hr`;
          // return `${resource?.rate ? `$${resource?.rate}/hr` : ""}`;
        },
      },
      {
        field: "customer_id",
        headerName: "Customer",
        minWidth: isMobile ? 150 : 200,
        flex: isMobile ? 0 : undefined,
        sortable: true,
        filterable: true,
        hide: isMobile,
        renderCell: (params: GridRenderCellParams<undefined, Project>) =>
          !!params.row.company ? (
            <Typography variant="body2" sx={{ fontSize: { xs: "0.875rem", sm: "0.95rem" } }}>{params.row.company.name}</Typography>
          ) : (
            <Typography variant="body2" sx={{ color: "text.disabled", fontSize: { xs: "0.875rem", sm: "0.95rem" } }}>n/a</Typography>
          ),
      },
      {
        field: "resources.hired_at",
        headerName: "Hired At",
        minWidth: isMobile ? 150 : 200,
        flex: isMobile ? 0 : undefined,
        sortable: false,
        filterable: false,
        hide: isMobile,
        valueGetter: (params: GridValueGetterParams<undefined, Project>) => {
          const resource = params.row.resources?.find(
            (item: ProjectResource) => item.resource_id === dataTalent?.id
          );
          return `${resource?.hired_at
            ? dayjs(resource?.hired_at).format("MMM DD, YYYY hh:mm A")
            : ""
            }`;
        },
      },
    ],
    [isMobile, dataTalent?.id]
  );

  const [query, setQuery] = React.useState<string | undefined>(undefined);
  const debounceQuery = useDebounce(query, 500);

  React.useEffect(() => {
    setSearchQuery(debounceQuery);
  }, [debounceQuery]);

  const onView = (item: any) => {
    setCurrentItem(item);
    setOpen(true);
  };

  return (
    <>
      <CardBase
        title="Jobs"
        useGlassCard={false}
        sx={{ height: "85vh", p: 0 }}
        headerActions={
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ xs: "stretch", sm: "center" }}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            <StyledTextField
              size="small"
              label="Enter to search.."
              value={query || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
              sx={{
                minWidth: { xs: "100%", sm: 250 },
                width: { xs: "100%", sm: "auto" },
              }}
            />
            {!!query && (
              <Button
                variant="text"
                onClick={() => setQuery("")}
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { xs: "100%", sm: "auto" },
                  color: "#F4A259",
                  "&:hover": {
                    backgroundColor: "rgba(244, 162, 89, 0.08)",
                  },
                }}
              >
                Reset
              </Button>
            )}
          </Stack>
        }
      >
        <GlassDataGrid
          rows={rows || []}
          columns={columns}
          paginationMode="server"
          rowCount={rowCount}
          page={page}
          onPageChange={(newPage: number) => setPage(newPage)}
          pageSize={pageSize}
          rowsPerPageOptions={[25, 50, 100]}
          onPageSizeChange={(newSize: number) => setPageSize(newSize)}
          sortModel={sortModel}
          sortingMode="server"
          onSortModelChange={(newSortModel: GridSortModel) => setSortModel(newSortModel)}
          loading={isLoading || isFetching}
          components={{
            LoadingOverlay: OrangeLinearProgress,
            NoResultsOverlay: NoResultsOverlay,
          }}
          disableSelectionOnClick
          disableColumnFilter
          height={isMobile ? "calc(85vh - 140px)" : "calc(85vh - 80px)"}
          sx={{
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "transparent",
              borderTop: "none",
              paddingLeft: { xs: 1, sm: 3 },
              paddingRight: { xs: 1, sm: 3 },
              "& .MuiDataGrid-columnHeaderTitle": {
                color: "#F4A259",
                fontWeight: 600,
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
              },
              "& .MuiDataGrid-columnHeader": {
                color: "#F4A259",
                "& .MuiDataGrid-iconButtonContainer": {
                  color: "#F4A259",
                },
                "& .MuiDataGrid-sortIcon": {
                  color: "#F4A259",
                },
              },
            },
            "& .MuiDataGrid-cell": {
              paddingLeft: { xs: 1, sm: 3 },
              paddingRight: { xs: 1, sm: 3 },
              py: { xs: 1, sm: 2 },
              fontSize: { xs: "0.75rem", sm: "0.875rem" },
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "rgba(244, 162, 89, 0.05)",
            },
            "& .MuiDataGrid-main": {
              padding: { xs: 1, sm: 2 },
            },
            "& .MuiDataGrid-footerContainer": {
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 1, sm: 0 },
            },
          }}
        />
      </CardBase>
      <ProjectDrawer data={currentItem!} open={open} setOpen={setOpen} />
    </>
  );
};
export default TalentJobsPanel;
